import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, RefreshCw, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';

function toMin(ms: number) {
  return Math.round(ms / 60000);
}

function fmtDur(ms?: number) {
  if (!ms || ms < 1) return '00:00';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

function uniqCount(arr: string[]) {
  return new Set(arr.filter(Boolean)).size;
}

function MetricCard({ title, value }: { title: string; value: string | number }) {
  return (
    <Card className="border-border bg-card">
      <CardHeader className="pb-1">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

export function DataRoomAnalytics({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<any[]>([]);
  const [summary, setSummary] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  async function load() {
    try {
      setLoading(true);
      const [{ data: agg }, { data: log }] = await Promise.all([
        supabase.rpc('dr_file_analytics', { p_company: companyId }),
        supabase
          .from('data_room_access_log')
          .select('id,file_id,email,started_at,duration_ms,success,geo,data_room_files(file_name)')
          .eq('company_id', companyId)
          .order('started_at', { ascending: false })
          .limit(500),
      ]);
      setSummary(agg || []);
      setRows(log || []);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter(
      (r) =>
        (r.email || '').toLowerCase().includes(term) ||
        (r.file_id || '').toLowerCase().includes(term)
    );
  }, [rows, q]);

  const maxAvgMs = useMemo(() => {
    const values = summary?.map((s: any) => s.avg_ms) || [0];
    return Math.max(...values);
  }, [summary]);

  useEffect(() => {
    load();
  }, [companyId]);

  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5" /> Data Room Analytics
        </CardTitle>
        <div className="flex gap-2">
          <Input
            placeholder="Search email/file"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary cards */}
        <div className="grid sm:grid-cols-3 gap-3">
          <MetricCard title="Files with Views" value={summary?.length || 0} />
          <MetricCard title="Top File Avg Dwell (min)" value={toMin(maxAvgMs)} />
          <MetricCard
            title="Unique Viewers (est.)"
            value={uniqCount(rows.map((r) => r.email)).toString()}
          />
        </div>

        {/* Top files bar chart */}
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={(summary || []).slice(0, 12)}>
              <XAxis dataKey="file_name" hide />
              <YAxis />
              <Tooltip />
              <Bar dataKey="views" name="Views" fill="hsl(var(--primary))" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Recent access table */}
        {loading ? (
          <div className="p-8 flex justify-center">
            <Loader2 className="animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Dwell (mm:ss)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const geo = r.geo as { city?: string; country?: string } | null;
                const location = geo
                  ? `${geo.city || ''}${geo.city && geo.country ? ', ' : ''}${geo.country || ''}`
                  : '—';

                return (
                  <TableRow key={r.id}>
                    <TableCell className="truncate max-w-[200px]">
                      {r.data_room_files?.file_name || r.file_id}
                    </TableCell>
                    <TableCell>{r.email || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{location}</TableCell>
                    <TableCell>{new Date(r.started_at).toLocaleString()}</TableCell>
                    <TableCell className="text-right">{fmtDur(r.duration_ms)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">No activity yet.</p>
        )}
      </CardContent>
    </Card>
  );
}
